// external
import React, { FC, useEffect, useRef, useState } from 'react';
import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import { Box, Tab, Tabs } from '@mui/material';

// internal
import useService from '../../hooks/service.hook';
import EquipmentService from '../../services/equipments.service';
import EquipmentModalHeader from './equipment-modal-header';
import EquipmentDetailsTab from './tabs/equipment-details-tab';
import EquipmentNotesTab from './tabs/equipment-notes-tab';
import EquipmentRaceLogTab from './tabs/equipment-race-log-tab';
import EquipmentManagersTab from './tabs/equipment-managers-tab';
import EquipmentUsageTab from './tabs/equipment-usage-tab';
import { palette } from '../../theme';

interface EquipmentModalProps {
  open: boolean;
  onClose: (refresh?: boolean) => void;
  equipmentId?: number;
  /** Tab index when the modal opens (0 Details, 1 Usage, 2 Notes, 3 Race Log, 4 Managers for owners). */
  initialTab?: number;
}

function flattenStandardFieldValues(raw: Record<string, unknown>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(raw).map(([k, v]) => [
      k,
      v != null && typeof v === 'object' && 'value' in v ? String((v as any).value ?? '') : String(v ?? ''),
    ])
  );
}

const EquipmentModal: FC<EquipmentModalProps> = (props) => {
  const [name, setName] = useState('');
  const [brand, setBrand] = useState('');
  const [year, setYear] = useState('');
  const [loading, setLoading] = useState(false);
  const [equipmentTypes, setEquipmentTypes] = useState<any[]>([]);
  const [selectedType, setSelectedType] = useState<any | null>(null);
  const [selectedSubtype, setSelectedSubtype] = useState<any | null>(null);
  const [subtypes, setSubtypes] = useState<any[]>([]);
  const [standardFields, setStandardFields] = useState<any[]>([]);
  const [standardFieldValues, setStandardFieldValues] = useState<{ [key: string]: string }>({});
  const [currentEquipment, setCurrentEquipment] = useState<any>(null);
  const [refreshParent, setRefreshParent] = useState(false);
  const [activeTab, setActiveTab] = useState<number>(0);
  const tabInitKeyRef = useRef<string>('');
  const equipmentService = useService(EquipmentService);

  const showManagersTab = Boolean(props.equipmentId && currentEquipment?.is_owner);

  useEffect(() => {
    if (!props.open) {
      tabInitKeyRef.current = '';
      return;
    }
    const key = `${props.equipmentId ?? 'new'}-${props.initialTab ?? 0}-${showManagersTab}`;
    const maxIdx = showManagersTab ? 4 : 3;
    if (tabInitKeyRef.current !== key) {
      let next = props.initialTab ?? 0;
      if (next > maxIdx) next = maxIdx;
      setActiveTab(next);
      tabInitKeyRef.current = key;
    } else {
      setActiveTab((t) => (t > maxIdx ? maxIdx : t));
    }
  }, [props.open, props.equipmentId, props.initialTab, showManagersTab]);

  useEffect(() => {
    if (!showManagersTab && activeTab > 3) setActiveTab(0);
  }, [showManagersTab, activeTab]);

  const applyUpdatedEquipment = (equipment: any) => {
    setCurrentEquipment(equipment);
    setName(equipment?.name ?? '');
    setBrand(equipment?.brand ?? '');
    setYear(equipment?.year != null ? String(equipment.year) : '');
    setStandardFieldValues(flattenStandardFieldValues(equipment?.standard_field_values ?? {}));
  };

  useEffect(() => {
    if (props.open) {
      equipmentService
        .getEquipmentTypes()
        .then((res) => {
          setEquipmentTypes(res.data.data || []);
        })
        .catch((error) => {
          console.error('Failed to fetch equipment types:', error);
        });
    }
  }, [props.open, equipmentService]);

  useEffect(() => {
    if (selectedType) {
      setSubtypes(selectedType.subtypes || []);
      setStandardFields(selectedType.standard_fields || []);
      if (!props.equipmentId) {
        setSelectedSubtype(null);
      }
    } else {
      setSubtypes([]);
      setStandardFields([]);
      setSelectedSubtype(null);
    }
  }, [selectedType, props.equipmentId]);

  useEffect(() => {
    if (selectedSubtype) {
      setStandardFields([...(selectedType?.standard_fields || []), ...(selectedSubtype.standard_fields || [])]);
    } else if (selectedType) {
      setStandardFields(selectedType.standard_fields || []);
    } else {
      setStandardFields([]);
    }
  }, [selectedSubtype, selectedType]);

  useEffect(() => {
    if (props.open) return;
    setCurrentEquipment(null);
    setName('');
    setBrand('');
    setYear('');
    setSelectedType(null);
    setSelectedSubtype(null);
    setStandardFieldValues({});
    setRefreshParent(false);
  }, [props.open]);

  useEffect(() => {
    if (!props.equipmentId) return;
    setLoading(true);
    equipmentService
      .getEquipment(props.equipmentId)
      .then((res) => {
        applyUpdatedEquipment(res.data.data);
      })
      .catch(() => {
        props.onClose();
      })
      .finally(() => {
        setLoading(false);
      });
  }, [props.equipmentId, props.open, props.onClose, equipmentService]);

  useEffect(() => {
    if (!props.equipmentId) {
      setStandardFieldValues({});
    }
  }, [selectedType, selectedSubtype, props.equipmentId]);

  useEffect(() => {
    if (props.equipmentId && equipmentTypes.length > 0 && currentEquipment) {
      const equipmentType = equipmentTypes.find(
        (t) => t.id === currentEquipment.equipment_type?.id || t.id === currentEquipment.equipment_type_id
      );
      if (equipmentType) {
        setSelectedType(equipmentType);
        if (currentEquipment.equipment_subtype) {
          const subtype = (equipmentType.subtypes || []).find((s) => s.id === currentEquipment.equipment_subtype.id);
          if (subtype) setSelectedSubtype(subtype);
        }
      }
      if (currentEquipment.standard_field_values) {
        setStandardFieldValues(flattenStandardFieldValues(currentEquipment.standard_field_values));
      }
    }
  }, [props.equipmentId, equipmentTypes, currentEquipment]);

  return (
    <Modal
      open={props.open}
      onClose={() => props.onClose(refreshParent)}
      aria-labelledby="equipment-modal-title"
      aria-describedby="equipment-modal-description"
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300 }}
    >
      <Paper
        sx={{
          width: 'min(680px, 95vw)',
          height: '90vh',
          maxHeight: '90vh',
          padding: 0,
          borderRadius: '20px',
          boxShadow: '0 24px 80px rgba(0,0,0,0.2)',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          overflow: 'hidden',
        }}
      >
        <EquipmentModalHeader
          equipment={currentEquipment}
          selectedType={selectedType}
          selectedSubtype={selectedSubtype}
          refreshParent={refreshParent}
          onClose={props.onClose}
          onEquipmentUpdate={(equipment) => {
            setCurrentEquipment(equipment);
            applyUpdatedEquipment(equipment);
          }}
        />

        <Tabs
          value={activeTab}
          onChange={(_e, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
          TabIndicatorProps={{ style: { display: 'none' } }}
          sx={{
            minHeight: 36,
            height: 36,
            mx: 2,
            marginTop: '12px',
            bgcolor: palette.grey[100],
            borderRadius: '10px',
            p: '3px',
            '.MuiTabs-flexContainer': { gap: '2px', height: '100%' },
            '.MuiTab-root': {
              minHeight: 0,
              height: '100%',
              fontSize: 12,
              color: palette.text.secondary,
              fontWeight: 500,
              padding: '0 8px',
              textTransform: 'none',
              borderRadius: '8px',
              transition: 'background 0.15s, color 0.15s',
              zIndex: 1,
            },
            '.Mui-selected': {
              color: palette.text.primary,
              fontWeight: 700,
              bgcolor: palette.background.paper,
              boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
            },
          }}
        >
          <Tab label="Details" />
          <Tab label="Usage" />
          <Tab label="Notes" />
          <Tab label="Race Log" />
          {showManagersTab && <Tab label="Managers" />}
        </Tabs>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            borderTop: `1px solid ${palette.divider}`,
            marginTop: '8px',
            padding: '12px 16px',
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
          }}
        >
          {activeTab === 0 && (
            <EquipmentDetailsTab
              equipmentId={props.equipmentId}
              loading={loading}
              currentEquipment={currentEquipment}
              equipmentData={{ name, brand, year, standardFieldValues }}
              equipmentConfig={{ selectedType, selectedSubtype, subtypes, equipmentTypes, standardFields }}
              onEquipmentUpdate={applyUpdatedEquipment}
              onSetRefreshParent={setRefreshParent}
            />
          )}
          {activeTab === 1 && (
            <EquipmentUsageTab
              equipmentId={props.equipmentId}
              currentEquipment={currentEquipment}
              onEquipmentUpdate={(equipment) => {
                setCurrentEquipment(equipment);
                applyUpdatedEquipment(equipment);
              }}
              onSetRefreshParent={setRefreshParent}
            />
          )}
          {activeTab === 2 && <EquipmentNotesTab equipmentId={props.equipmentId} />}
          {activeTab === 3 && <EquipmentRaceLogTab equipmentId={props.equipmentId} />}
          {showManagersTab && activeTab === 4 && (
            <EquipmentManagersTab
              equipmentId={props.equipmentId}
              managers={currentEquipment?.managers}
              onEquipmentUpdate={(equipment) => {
                setCurrentEquipment(equipment);
                applyUpdatedEquipment(equipment);
              }}
            />
          )}
        </Box>
      </Paper>
    </Modal>
  );
};

export default EquipmentModal;
